import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import UserPreferencesService from '../../services/core/UserPreferencesService';

const languages = [
  { code: 'en', name: 'English', countryCode: 'US' },
  { code: 'es', name: 'Español', countryCode: 'ES' },
  { code: 'ca', name: 'Català', countryCode: 'ES-CT' },
  { code: 'eu', name: 'Euskara', flagClass: 'basque-flag' },
  { code: 'gl', name: 'Galego', countryCode: 'ES-GA' },
  { code: 'pt', name: 'Português', countryCode: 'PT' },
  { code: 'pt-BR', name: 'Português (Brasil)', countryCode: 'BR' },
  { code: 'fr', name: 'Français', countryCode: 'FR' },
  { code: 'it', name: 'Italiano', countryCode: 'IT' },
  { code: 'de', name: 'Deutsch', countryCode: 'DE' },
  { code: 'jp', name: '日本語', countryCode: 'JP' },
  { code: 'zh', name: '中文', countryCode: 'CN' },
];

const normalizeLanguage = (lng) => {
  if (!lng) return 'en';
  const normalized = String(lng).toLowerCase();
  if (normalized === 'pt-br' || normalized.startsWith('pt-br-')) return 'pt-BR';
  return normalized.split('-')[0];
};

const renderLanguageFlag = (lang, large = false) => {
  if (lang.flagClass) {
    return <span className={`${lang.flagClass} ${large ? 'large' : ''}`} aria-hidden="true" />;
  }
  if (lang.flag) {
    return <span style={{ fontSize: large ? '1.5em' : '1.2em' }}>{lang.flag}</span>;
  }
  return (
    <ReactCountryFlag
      countryCode={lang.countryCode}
      svg
      style={{
        width: large ? '1.5em' : '1.2em',
        height: large ? '1.5em' : '1.2em',
      }}
    />
  );
};

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const activeLanguageCode = useMemo(() => normalizeLanguage(i18n.resolvedLanguage || i18n.language), [i18n.language, i18n.resolvedLanguage]);

  const currentLanguage =
    languages.find((lang) => lang.code === activeLanguageCode) || languages[0];

  const changeLanguage = async (langCode) => {
    await i18n.changeLanguage(langCode);
    window.dispatchEvent(new CustomEvent("idaxLanguageChanged", { detail: { language: langCode } }));
    UserPreferencesService.rememberLanguage(langCode).catch(() => {});
    setIsOpen(false);
  };

  return (
    <div className="language-selector">
      <button
        className="language-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title={currentLanguage.name}
      >
        <span className="language-flag">
          {renderLanguageFlag(currentLanguage)}
        </span>
        <i className={`fa fa-chevron-down language-chevron ${isOpen ? 'open' : ''}`}></i>
      </button>

      {isOpen && (
        <>
          <div className="language-overlay" onClick={() => setIsOpen(false)}></div>
          <div className="language-menu">
            {languages.map((lang) => {
              const isActive = activeLanguageCode === lang.code;
              return (
                <button
                  key={lang.code}
                  className={`language-item ${isActive ? 'active' : ''}`}
                  onClick={() => changeLanguage(lang.code)}
                >
                  <span className="language-flag-large">
                    {renderLanguageFlag(lang, true)}
                  </span>
                  <span className="language-full-name">{lang.name}</span>
                  {isActive && <i className="fa fa-check language-check"></i>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
